import {
  CountryDto,
  TestLaboratoryDto,
  TestLaboratorySearchRequest,
  UpsertTestLaboratoryDto
} from 'ts-types/api.types';
import { MessageSourceProps, withMessages } from 'react-message-source';
import axios, { CancelTokenSource } from 'axios';
import React, { Component } from 'react';
import { noop } from 'utils/functionUtils';
import { deleteTestLaboratory, findTestLaboratoryById, searchTestLaboratory } from 'services/testLaboratoryServices';
import { debounce } from 'ts-debounce';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import LoaderComponent from 'components/LoaderComponent';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import PxGrid from 'components/layout/PxGrid';
import { Grid, Modal } from 'semantic-ui-react';
import Input from 'components/final-form/Input';
import CheckBox from 'components/final-form/CheckBox';
import AddButton from 'components/AddButton';
import EditActionButton from 'components/EditActionButton';
import DeleteActionButton from 'components/DeleteActionButton';
import { FormState } from 'final-form';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import UpsertTestLaboratoryForm from 'components/medicaments/testlaboratiry/UpsertTestLaboratoryForm';
import { getAllCountries } from 'services/countryServices';
import SearchResultComponent from 'components/SearchResultComponent';

const initialTestLaboratorySearch: Partial<TestLaboratorySearchRequest> = {
  includesInactive: false
};

interface TestLaboratoryFieldProps extends FormState {
  values: Partial<TestLaboratorySearchRequest>
}

interface Props extends MessageSourceProps {
}

interface State {
  referenceDataLoaded: boolean,
  searchValues: Partial<TestLaboratorySearchRequest>
  countries: Array<CountryDto>,

  foundTestLaboratory: Array<TestLaboratoryDto>,
  testLaboratoriesLoaded: boolean,

  addEditModalOpen: boolean,
  testLaboratory?: UpsertTestLaboratoryDto,
  testLaboratoryId?: number,

  cancelTokenSource: CancelTokenSource
}

class TestLaboratoryView extends Component<Props, State> {

  private mounted: boolean = false;

  private initialSearchValues: Partial<TestLaboratorySearchRequest> = {
    searchKey: "",
    includesInactive: false
  }

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      referenceDataLoaded: false,

      searchValues: {},

      countries: [],

      foundTestLaboratory: [],
      testLaboratoriesLoaded: false,

      addEditModalOpen: false,

      cancelTokenSource
    }

    this.loadReferenceData(cancelTokenSource);
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  }

  loadReferenceData = async (cancelTokenSource: CancelTokenSource) => {
    try {
      const countries = await getAllCountries(cancelTokenSource);

      this.setState({
        countries: countries ? countries : []
      });

    } finally {
      this.setState({
        referenceDataLoaded: true
      });
    }
  }

  openAddEditModal = (id: number | undefined) => {

    const {cancelTokenSource} = this.state;

    if (id) {
      findTestLaboratoryById(id, cancelTokenSource).then(response => {
        this.showModal(id, response);
      }).catch(noop);
    }
  };

  showModal = (id?: number, testLaboratory?: UpsertTestLaboratoryDto) => {
    this.setState(({}: State) => {
      return {
        addEditModalOpen: true,
        testLaboratory: testLaboratory,
        testLaboratoryId: id
      }
    });
  };

  hideModal = () => {
    this.setState(({}: State) => ({
      addEditModalOpen: false,
      testLaboratory: undefined,
      testLaboratoryId: undefined
    }));
  };

  onSaveUpsertForm = () => {
    const {searchValues, cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        testLaboratoriesLoaded: true
      })
    };

    searchTestLaboratory(searchValues, cancelTokenSource)
        .then(response => {
          this.setState({
            foundTestLaboratory: response
          });
        })
        .catch(noop)
        .finally(onFinally);

    this.hideModal();
  };

  handleChange = debounce(({values}: TestLaboratoryFieldProps): void => {
    if (!this.mounted) {
      return;
    }

    this.setState({
      searchValues: values
    });

    const {cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        testLaboratoriesLoaded: true
      })
    };

    this.setState({testLaboratoriesLoaded: false});
    searchTestLaboratory(values, cancelTokenSource)
        .then(response =>
            this.setState({
              foundTestLaboratory: response
            })
        )
        .catch(noop)
        .finally(onFinally);
  }, 300)

  deleteTestLaboratory = (id: number): void => {
    const {cancelTokenSource} = this.state;
    deleteTestLaboratory(id, cancelTokenSource).then(response => {
      this.setState(({foundTestLaboratory}: State) => ({
        foundTestLaboratory: foundTestLaboratory.filter((testLaboratory: TestLaboratoryDto) => testLaboratory.id != response.id)
      }))
    }).catch(noop);
  };

  render(): React.ReactNode {
    const {getMessage} = this.props;
    const {testLaboratoriesLoaded} = this.state;

    return (
        <PraxStyledSegment style={{height: "100%"}}>
          <div>
            {this.renderSearchTestLaboratorySegment()}
          </div>
          <StyledViewTableRow>
            {!testLaboratoriesLoaded
                ? <LoaderComponent message={getMessage("testLaboratory.loading")}/>
                : this.renderTestLaboratorySegment()}
          </StyledViewTableRow>
          {this.renderUpsertTestLaboratoryModal()}
        </PraxStyledSegment>
    );
  }

  renderSearchTestLaboratorySegment = (): JSX.Element => {
    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={this.initialSearchValues}
            subscription={{pristine: true, values: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {foundTestLaboratory} = this.state;

    const numResults = foundTestLaboratory.length;

    return (
        <PxGrid columns={2}>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("search")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="searchKey"
                  component={Input}
                  autoFocus
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
            </Grid.Column>
            <Grid.Column width={4} verticalAlign={"middle"}>
              <Field
                  name="includesInactive"
                  component={CheckBox}
                  label={getMessage("includingInactive")}
                  toggle={true}
              />
            </Grid.Column>
            <Grid.Column width={9} textAlign="right">
              <AddButton buttonMessage={getMessage("button.add")}
                         onClick={() => this.showModal()}/>
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleChange}/>
        </PxGrid>
    )
  };

  statusCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;
    if (cellData) {

      return getMessage("testLaboratory.active");
    }
    return getMessage("testLaboratory.inactive");
  };

  basicLaboratoryCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;
    if (cellData) {

      return getMessage("testLaboratory.basic");
    }
    return "";
  }

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.openAddEditModal(cellData)}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("testLaboratory.confirmDelete")}
              onConfirm={() => this.deleteTestLaboratory(cellData)}
          />
        </React.Fragment>
    );
  };

  testLaboratoryRowGetter = ({index}: any) => {
    const {foundTestLaboratory} = this.state;

    Object.assign(foundTestLaboratory[index], {index: index + 1});

    return foundTestLaboratory[index];
  };

  testLaboratoryRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};

    const {foundTestLaboratory} = this.state;

    const testLaboratoryId = foundTestLaboratory[index].id;

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(testLaboratoryId)}
        >
          {columns}
        </div>
    );
  };

  renderTestLaboratorySegment = (): JSX.Element => {

    const {getMessage} = this.props;
    const {foundTestLaboratory} = this.state;
    return (
        <VirtualizedTable
            rowCount={foundTestLaboratory.length}
            rowGetter={this.testLaboratoryRowGetter}
            rowRenderer={this.testLaboratoryRowRenderer}
            columns={[
              {
                width: 300,
                label: (getMessage("testLaboratory.name")),
                dataKey: "name"
              },
              {
                width: 300,
                label: (getMessage("testLaboratory.street")),
                dataKey: "street"
              },
              {
                width: 100,
                label: (getMessage("testLaboratory.zip")),
                dataKey: "zip"
              },
              {
                width: 300,
                label: (getMessage("testLaboratory.city")),
                dataKey: "city"
              },
              {
                width: 300,
                label: (getMessage("testLaboratory.remarks")),
                dataKey: "remarks"
              },
              {
                width: 200,
                label: (getMessage("testLaboratory.basicLaboratory")),
                dataKey: "basicLaboratory",
                cellRenderer: this.basicLaboratoryCellRenderer
              },
              {
                width: 150,
                flexGrow: 1,
                label: (getMessage("testLaboratory.status")),
                dataKey: "active",
                cellRenderer: this.statusCellRenderer
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />

    );
  };

  renderUpsertTestLaboratoryModal = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {addEditModalOpen, testLaboratory, testLaboratoryId, countries} = this.state;


    return (
        <Modal
            open={addEditModalOpen}
            onClose={() => this.hideModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("testLaboratory.title")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertTestLaboratoryForm
                testLaboratory={testLaboratory}
                testLaboratoryId={testLaboratoryId}
                countries={countries}
                onCancel={this.hideModal}
                onSave={this.onSaveUpsertForm}
            />
          </FormModalContentContainer>
        </Modal>
    );
  };

}

export default withMessages(TestLaboratoryView);
